// 어느 화면에서든 여는 시트 — v0.3 의 SessionSheet·AddWarnSheet·StudentFormSheet·AddItemsBox·StaffFormSheet
import { useState, useSyncExternalStore } from "react";
import { addItems, addWarn, currentMe, deleteSession, deleteStaff, deleteStudent, moveSession, saveStaff, saveStudent } from "../data/actions";
import { itemsFor, warnsOf } from "../data/select";
import { useView } from "../data/store";
import { WARN_TYPES, type Slot, type Student } from "../data/types";
import { addDays, DOW, fmtDate, todayStr, weekOfMonth } from "../lib/date";
import { go } from "../ui/hooks";
import { Icon } from "../ui/icons";
import { Pill, StatusPill, StudentName, toast } from "../ui/parts";
import { Sheet } from "../ui/Sheet";

/* ───────── 시트 열기(전역) ───────── */
export type SheetReq =
  | { kind: "session"; id: string }
  | { kind: "warn"; studentId: string | null }
  | { kind: "student"; id: string | null }
  | { kind: "staff"; id: string | null };
let cur: SheetReq | null = null;
const ls = new Set<() => void>();
export const openSheet = (r: SheetReq | null) => { cur = r; ls.forEach((f) => f()); };
const close = () => openSheet(null);

export function SheetHost() {
  const r = useSyncExternalStore((f) => { ls.add(f); return () => ls.delete(f); }, () => cur);
  if (!r) return null;
  switch (r.kind) {
    case "session": return <SessionSheet key={r.id} id={r.id} />;
    case "warn": return <WarnSheet key={r.studentId ?? ""} studentId={r.studentId} />;
    case "student": return <StudentForm key={r.id ?? "new"} id={r.id} />;
    case "staff": return <StaffForm key={r.id ?? "new"} id={r.id} />;
  }
}

/* ───────── 일정 상세: 날짜·시간 변경, 삭제 ───────── */
function SessionSheet({ id }: { id: string }) {
  const v = useView();
  const s = v.sessions.find((x) => x.id === id);
  const st = s ? v.students.find((x) => x.id === s.studentId) : undefined;
  if (!s || !st) return <Sheet title="개별일정" onClose={close}><p className="muted">삭제됐거나 없는 일정입니다.</p></Sheet>;
  const items = itemsFor(v, s);
  return (
    <Sheet title="개별일정" onClose={close}>
      <div className="kv-head">
        <span className="time">{fmtDate(s.date)} {s.time}</span>
        <StudentName st={st} />
        <span className="grow" />
        {s.late ? <Pill color="var(--color-late)">지각</Pill> : null}
        <StatusPill status={s.status} />
      </div>
      <p className="muted small">등록 {s.createdBy || "-"}{s.closedBy ? ` · 확인 ${s.closedBy}` : ""}{s.note ? ` · ${s.note}` : ""}</p>
      <p className="label">이 일정의 시험지</p>
      {items.length ? <ul className="plain">{items.map((i) => <li key={i.id}>{i.done ? "✓ " : "☐ "}[{i.week}] {i.title}</li>)}</ul> : <p className="muted">남은 시험지 없음</p>}
      <p className="label">일정 변경</p>
      <div className="form-row">
        <label className="field grow"><span className="label">날짜</span>
          <input className="input" type="date" value={s.date} onChange={(e) => e.target.value && moveSession(s, e.target.value, s.time)} /></label>
        <label className="field grow"><span className="label">시간</span>
          <input className="input" type="time" step={600} value={s.time} onChange={(e) => e.target.value && moveSession(s, s.date, e.target.value)} /></label>
      </div>
      <div className="form-actions">
        <button type="button" className="btn btn-danger" onClick={() => {
          if (!confirm(`${st.name} ${fmtDate(s.date)} ${s.time} 일정을 삭제합니다. 이 일정에서 통과한 시험지는 미완료로 돌아갑니다.`)) return;
          deleteSession(s.id); close(); toast("삭제했습니다");
        }}>일정 삭제</button>
        <button type="button" className="btn btn-primary grow" onClick={() => { close(); go(`students/${encodeURIComponent(st.id)}`); }}>{st.name} 학생 보기</button>
      </div>
    </Sheet>
  );
}

/* ───────── 경고카드 기록 ───────── */
function WarnSheet({ studentId }: { studentId: string | null }) {
  const v = useView();
  const [sid, setSid] = useState(studentId);
  const [type, setType] = useState<string>("수업 지각");
  const [date, setDate] = useState(todayStr());
  const [note, setNote] = useState("");
  const [q, setQ] = useState("");
  const st = v.students.find((s) => s.id === sid);
  const th = v.settings.warnThreshold;
  return (
    <Sheet title="경고카드 기록" onClose={close} footer={
      <button type="button" className="btn btn-primary btn-lg grow" disabled={!st} onClick={() => {
        if (!st) return;
        addWarn(st.id, type, date, note.trim());
        const n = warnsOf(v, st.id).length + 1;
        close(); toast(`${st.name} ${type} 기록 · 누적 ${n}장${n >= th ? " (기준 이상)" : ""}`);
      }}>기록</button>
    }>
      <p className="label">학생</p>
      {!st ? (
        <>
          <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="이름 검색" aria-label="학생 검색" />
          <div className="chips mt">
            {v.students.filter((s) => !q.trim() || s.name.includes(q.trim())).map((s) => (
              <button key={s.id} type="button" className="chip" onClick={() => setSid(s.id)}><StudentName st={s} school={false} /></button>
            ))}
          </div>
        </>
      ) : (
        <div className="kv-head">
          <StudentName st={st} />
          <span className="grow" />
          <span className="muted small">현재 {warnsOf(v, st.id).length}장</span>
          {studentId ? null : <button type="button" className="btn btn-ghost" onClick={() => setSid(null)}>변경</button>}
        </div>
      )}
      <p className="label">종류</p>
      <div className="chips">
        {WARN_TYPES.map((t) => <button key={t} type="button" className={"chip" + (type === t ? " on" : "")} aria-pressed={type === t} onClick={() => setType(t)}>{t}</button>)}
      </div>
      <p className="label">날짜</p>
      <div className="chips">
        <button type="button" className={"chip" + (date === todayStr() ? " on" : "")} onClick={() => setDate(todayStr())}>오늘</button>
        <button type="button" className={"chip" + (date === addDays(todayStr(), -1) ? " on" : "")} onClick={() => setDate(addDays(todayStr(), -1))}>어제</button>
        <input className="input input-inline" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="다른 날" />
      </div>
      <label className="field"><span className="label">메모 (선택)</span>
        <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="예: 15분 지각, 부교재 7강 숙제" /></label>
    </Sheet>
  );
}

/* ───────── 시험지 여러 개 빠르게 추가 ───────── */
export function AddItemsBox({ studentId, onDone }: { studentId: string; onDone: () => void }) {
  const v = useView();
  const [week, setWeek] = useState(`${weekOfMonth()}주차`);
  const [picked, setPicked] = useState<{ week: string; title: string }[]>([]);
  const [custom, setCustom] = useState("");
  return (
    <div className="add-items boxed">
      <div className="chips">
        {[1, 2, 3, 4, 5].map((n) => `${n}주차`).map((w) => <button key={w} type="button" className={"chip" + (week === w ? " on" : "")} aria-pressed={week === w} onClick={() => setWeek(w)}>{w}</button>)}
      </div>
      <div className="chips">
        {v.settings.presets.map((p) => {
          const on = picked.some((i) => i.week === week && i.title === p);
          return <button key={p} type="button" className={"chip" + (on ? " on" : "")} aria-pressed={on} onClick={() => setPicked(on ? picked.filter((i) => !(i.week === week && i.title === p)) : [...picked, { week, title: p }])}>{p}</button>;
        })}
      </div>
      <form className="inline-form" onSubmit={(e) => { e.preventDefault(); if (custom.trim()) { setPicked([...picked, { week, title: custom.trim() }]); setCustom(""); } }}>
        <input className="input" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="직접 입력" aria-label="시험지 직접 입력" />
        <button type="submit" className="btn">추가</button>
      </form>
      {picked.length ? <p className="muted small">추가될 항목: {picked.map((i) => `[${i.week}] ${i.title}`).join(", ")}</p> : null}
      <button type="button" className="btn btn-primary" disabled={!picked.length} onClick={() => { addItems(studentId, picked); toast(`${picked.length}개 추가했습니다`); onDone(); }}>
        {picked.length ? `${picked.length}개 추가` : "시험지를 고르세요"}
      </button>
    </div>
  );
}

/* ───────── 학생 추가·수정 ───────── */
function StudentForm({ id }: { id: string | null }) {
  const v = useView();
  const s = id ? v.students.find((x) => x.id === id) ?? null : null;
  const [name, setName] = useState(s?.name ?? "");
  const [school, setSchool] = useState(s?.school ?? "");
  const [memo, setMemo] = useState(s?.memo ?? "");
  const [slots, setSlots] = useState<Slot[]>(() => [...(s?.slots ?? [])].sort((a, b) => a.dow - b.dow || a.time.localeCompare(b.time)));
  const save = () => {
    if (!name.trim()) { toast("이름을 입력하세요", { tone: "error" }); return; }
    const nid = saveStudent(s as Student | null, { name: name.trim(), school: school.trim(), memo: memo.trim(), slots }, v.students.length);
    close(); toast("저장했습니다");
    if (!s) go(`students/${encodeURIComponent(nid)}`);
  };
  return (
    <Sheet title={s ? "학생 정보 수정" : "학생 추가"} onClose={close} footer={
      <>
        {s ? <button type="button" className="btn btn-danger" onClick={() => {
          if (!confirm(`${s.name} 학생을 삭제합니다. 일정·클리닉·경고카드 기록이 모두 함께 삭제됩니다.`)) return;
          deleteStudent(s.id); close(); go("students"); toast("삭제했습니다");
        }}>삭제</button> : null}
        <button type="button" className="btn btn-primary btn-lg grow" onClick={save}>저장</button>
      </>
    }>
      <div className="form-row">
        <label className="field grow"><span className="label">이름</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="field grow"><span className="label">학교·학년</span><input className="input" value={school} onChange={(e) => setSchool(e.target.value)} placeholder="예: 가람고1" /></label>
      </div>
      <label className="field"><span className="label">연락처 — 시험판은 가짜 데이터라 비워 둡니다</span><input className="input" value="" disabled placeholder="(비움)" /></label>
      <p className="label">정규 수업 시간 — 개별일정 등록 때 기본 날짜·시간으로 제안됩니다</p>
      {slots.length === 0 ? <p className="muted small">등록된 시간이 없습니다.</p> : null}
      {slots.map((sl, i) => (
        <div className="form-row slot-row" key={i}>
          <select className="input" value={sl.dow} aria-label="요일" onChange={(e) => setSlots(slots.map((x, j) => j === i ? { ...x, dow: Number(e.target.value) } : x))}>
            {DOW.map((d, k) => <option key={d} value={k + 1}>{d}요일</option>)}
          </select>
          <input className="input" type="time" step={600} value={sl.time} aria-label="시간" onChange={(e) => e.target.value && setSlots(slots.map((x, j) => j === i ? { ...x, time: e.target.value } : x))} />
          <button type="button" className="btn btn-ghost btn-icon" aria-label="이 시간 삭제" onClick={() => setSlots(slots.filter((_, j) => j !== i))}><Icon name="close" /></button>
        </div>
      ))}
      <button type="button" className="btn btn-ghost" onClick={() => setSlots([...slots, { dow: 1, time: "16:00" }])}><Icon name="plus" size={18} /> 시간 추가</button>
      <label className="field"><span className="label">메모</span><input className="input" value={memo} onChange={(e) => setMemo(e.target.value)} /></label>
    </Sheet>
  );
}

/* ───────── 직원 추가·수정(관리자) ───────── */
function StaffForm({ id }: { id: string | null }) {
  const v = useView();
  const s = id ? v.staff.find((x) => x.id === id) ?? null : null;
  const me = currentMe();
  const isMe = !!s && s.id === me?.id;
  const [name, setName] = useState(s?.name ?? "");
  const [pin, setPin] = useState("");
  const [admin, setAdmin] = useState(s?.admin ?? false);
  const save = () => {
    if (!name.trim()) { toast("이름을 입력하세요", { tone: "error" }); return; }
    if (!s && !/^\d{4,8}$/.test(pin)) { toast("새 직원은 PIN 4~8자리가 필요합니다", { tone: "error" }); return; }
    if (pin && !/^\d{4,8}$/.test(pin)) { toast("PIN 은 숫자 4~8자리", { tone: "error" }); return; }
    saveStaff(s?.id ?? null, name.trim(), admin, pin || null);
    close(); toast("저장했습니다");
  };
  return (
    <Sheet title={s ? "직원 정보" : "직원 추가"} onClose={close} footer={
      <>
        {s && !isMe ? <button type="button" className="btn btn-danger" onClick={() => {
          if (!confirm(`${s.name} 계정을 삭제합니다. 이미 기록된 등록자 이름은 그대로 남습니다.`)) return;
          deleteStaff(s.id); close(); toast("삭제했습니다");
        }}>삭제</button> : null}
        <button type="button" className="btn btn-primary btn-lg grow" onClick={save}>저장</button>
      </>
    }>
      <label className="field"><span className="label">이름</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="field"><span className="label">{s ? "새 PIN (바꿀 때만, 숫자 4~8자리)" : "PIN (숫자 4~8자리)"}</span>
        <input className="input" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))} /></label>
      <button type="button" role="switch" aria-checked={admin} disabled={isMe} className={"switch mt" + (admin ? " on" : "")} onClick={() => setAdmin(!admin)}>
        <span className="switch-track"><span className="switch-thumb" /></span>
        <span>관리자 권한 {isMe ? "(내 계정은 바꿀 수 없음)" : ""}</span>
      </button>
      <p className="muted small">관리자는 직원·시험지 목록·데이터 관리 화면을 볼 수 있습니다. PIN 은 서버에 해시로만 저장되어 다시 볼 수 없습니다.</p>
    </Sheet>
  );
}
